import React, { useEffect, useState } from "react";
import { addScheduleApi, updateScheduleApi } from "../../../apis/apis.hrm/apisSchedule";
import { getRolesApi } from "../../../apis/apis.hrm/apisRoles";
import { toast } from "react-toastify";

const inputClass = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelClass = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

export function FragmentScheduleForm({ schedule, onSuccess }) {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        scheduleId: schedule?.scheduleId || "",
        roleId: schedule?.roleId || "",
        startTime: schedule?.startTime || "",
        endTime: schedule?.endTime || "",
        daysOfWeek: schedule?.daysOfWeek || "LUNES-MARTES-MIERCOLES-JUEVES-VIERNES-SABADO",
        hasRestrictions: schedule?.hasRestrictions ?? true,
    });

    const loadRoles = async () => {
        const response = await getRolesApi();
        setRoles(response);
    };

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        setFormData({
            scheduleId: schedule?.scheduleId || "",
            roleId: schedule?.roleId || "",
            startTime: schedule?.startTime || "",
            endTime: schedule?.endTime || "",
            daysOfWeek: schedule?.daysOfWeek || "LUNES-MARTES-MIERCOLES-JUEVES-VIERNES-SABADO",
            hasRestrictions: schedule?.hasRestrictions ?? true,
        });
    }, [schedule]);

    const addSchedule = async () => {
        setLoading(true);
        try {
            const response = await addScheduleApi(formData);

            // Verificar si la respuesta indica un error
            if (response.status === 400 || response.status === 500) {
                // Mostrar notificación de error con el mensaje del servidor
                toast.error(response.message, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return; // No continuar con el flujo de éxito
            }

            // Mostrar notificación de éxito
            toast.success("Horario creado exitosamente", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            onSuccess();
        } catch (error) {
            console.error("Error adding schedule:", error);

            // Mostrar notificación de error con el mensaje del servidor
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Error al crear el horario";
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const updateSchedule = async () => {
        setLoading(true);
        try {
            const response = await updateScheduleApi(formData);

            // Verificar si la respuesta indica un error
            if (response.status === 400 || response.status === 500) {
                // Mostrar notificación de error con el mensaje del servidor
                toast.error(response.message, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return; // No continuar con el flujo de éxito
            }

            // Mostrar notificación de éxito
            toast.success("Horario actualizado exitosamente", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            onSuccess();
        } catch (error) {
            console.error("Error updating schedule:", error);

            // Mostrar notificación de error con el mensaje del servidor
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Error al actualizar el horario";
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (schedule?.scheduleId) {
            await updateSchedule();
        } else {
            await addSchedule();
        }
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="space-y-4 px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Rol */}
                    <div>
                        <label htmlFor="roleId" className={labelClass}>
                            <i className="fas fa-user-tie mr-2 text-blue-500"></i>
                            Rol
                        </label>
                        <select
                            id="roleId"
                            className={inputClass}
                            value={formData.roleId}
                            onChange={(e) =>
                                handleInputChange("roleId", e.target.value)
                            }
                            required
                        >
                            <option value="">-- Seleccionar Rol --</option>
                            {roles.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Días de la semana */}
                    <div>
                        <label htmlFor="daysOfWeek" className={labelClass}>
                            <i className="fas fa-calendar-week mr-2 text-blue-500"></i>
                            Días de la Semana
                        </label>
                        <select
                            id="daysOfWeek"
                            className={inputClass}
                            value={formData.daysOfWeek}
                            onChange={(e) =>
                                handleInputChange("daysOfWeek", e.target.value)
                            }
                            required
                        >
                            <option value="LUNES-MARTES-MIERCOLES-JUEVES-VIERNES-SABADO">
                                Lunes a Sábado
                            </option>
                            <option value="LUNES-MARTES-MIERCOLES-JUEVES-VIERNES">
                                Lunes a Viernes
                            </option>
                            <option value="SABADO-DOMINGO">Sábado y Domingo</option>
                            <option value="LUNES-MARTES-MIERCOLES-JUEVES-VIERNES-SABADO-DOMINGO">
                                Todos los días
                            </option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Hora de inicio */}
                    <div>
                        <label htmlFor="startTime" className={labelClass}>
                            <i className="fas fa-clock mr-2 text-green-500"></i>
                            Hora de Inicio
                        </label>
                        <input
                            id="startTime"
                            type="time"
                            className={inputClass}
                            value={formData.startTime}
                            onChange={(e) =>
                                handleInputChange("startTime", e.target.value)
                            }
                            required
                        />
                    </div>

                    {/* Hora de fin */}
                    <div>
                        <label htmlFor="endTime" className={labelClass}>
                            <i className="fas fa-clock mr-2 text-red-500"></i>
                            Hora de Fin
                        </label>
                        <input
                            id="endTime"
                            type="time"
                            className={inputClass}
                            value={formData.endTime}
                            onChange={(e) =>
                                handleInputChange("endTime", e.target.value)
                            }
                            required
                        />
                    </div>
                </div>

                {/* Restricciones */}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-grow-1">
                            <div className="flex items-center gap-2 mb-1">
                                <i
                                    className={`fas ${
                                        formData.hasRestrictions
                                            ? "fa-exclamation-triangle text-amber-500"
                                            : "fa-check-circle text-green-500"
                                    }`}
                                ></i>
                                <label
                                    htmlFor="hasRestrictions"
                                    className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-0"
                                >
                                    {formData.hasRestrictions
                                        ? "Restricciones Activas"
                                        : "Sin Restricciones"}
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formData.hasRestrictions
                                    ? "Este horario tiene limitaciones adicionales de uso"
                                    : "Este horario puede ser usado sin restricciones especiales"}
                            </p>
                        </div>
                        <div className="flex items-center">
                            <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                                <input
                                    type="checkbox"
                                    id="hasRestrictions"
                                    checked={formData.hasRestrictions}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "hasRestrictions",
                                            e.target.checked
                                        )
                                    }
                                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 appearance-none cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none"
                                />
                                <label
                                    htmlFor="hasRestrictions"
                                    className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors ease-in-out duration-200 ${
                                        formData.hasRestrictions
                                            ? "bg-amber-500"
                                            : "bg-gray-300"
                                    }`}
                                ></label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={() => onSuccess()}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors"
                    >
                        <i className="fas fa-times"></i>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></div>
                                {schedule?.scheduleId
                                    ? "Actualizando..."
                                    : "Creando..."}
                            </>
                        ) : (
                            <>
                                <i
                                    className={`fas ${
                                        schedule?.scheduleId
                                            ? "fa-save"
                                            : "fa-plus"
                                    }`}
                                ></i>
                                {schedule?.scheduleId
                                    ? "Actualizar Horario"
                                    : "Crear Horario"}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
}
